import { ref } from "vue";
import { defineStore } from "pinia";
import { useToasterStore } from "@comp-lib/stores";
import { isDns, isUrl } from "@core/validation";
import type { EmailPreferences, RawEmailSettings } from "@/types";
import type { ImageData } from "@/components/ImageUploader.vue";
import { HttpError } from "@core/http";
import {
  fetchEmailPreferences,
  saveEmailPreferences,
  sendTestEmail,
  updateCwaccSettings,
  verifyEmailDomain,
  addDkimDomain,
  removeDkimDomain,
  verifyDkimDomain
} from "@/services/legacyEmailSettingsService";
import { getGatewaySettings } from "@/services/paymentsService";
import { mapToEmailSettings } from "@/composables/mapToEmailSettings";
import { mapToRawSmtpSettings } from "@/composables/mapToRawSmtpSettings";
import { useCurrentTenant } from "@/composables/useCurrentTenant";

export const useEmailSettingsStore = defineStore("legacyEmailSettings", () => {
  const currentTenant = useCurrentTenant();
  
  const toasterStore = useToasterStore();
  const { triggerNotif } = toasterStore;

  const companyLogoImage = ref<ImageData | null>(null);
  const previewLogo = ref("");
  const dnsDomain = ref("");
  const loading = ref(false);
  const verifiedPaymentFqdn = ref("");
  
  const loadingStates = ref({
    deletingDomains: {} as Record<string, boolean>,
    verifyingDomains: {} as Record<string, boolean>,
    addingDomain: false,
    fetchingSettings: false,
    updatingSettings: false,
    testingEmail: false,
    verifyingPaymentFqdn: false
  });

  const initialEmailPreferences = ref<EmailPreferences>({
    companyLogo: null,
    companyWebsite: "",
    paymentFqdn: "",
    paymentFqdnStatus: "",
    mailingAddress: "",
    DkimFromName: "",
    DkimEmailAddress: "",
    mailServer: "default",
    smtpSettings: {
      fromName: "",
      emailAddress: "",
      smtpServer: "",
      username: "",
      password: "",
      encryptionType: "SSL",
      portNumber: ""
    },
    defaultSettings: {
      data: [],
      dkimEmailDomain: "",
      dkimEmailPrefix: "",
      dkimFromName: ""
    },
    isConfigured: false
  });

  const emailPreferences = ref<EmailPreferences>(JSON.parse(JSON.stringify(initialEmailPreferences.value)));

  async function getEmailSettings(): Promise<RawEmailSettings> {
    loading.value = true;
    loadingStates.value.fetchingSettings = true;
    
    try {
      const result = await fetchEmailPreferences(currentTenant.value.id);
      
      initialEmailPreferences.value = mapToEmailSettings(result);
      emailPreferences.value = JSON.parse(JSON.stringify(initialEmailPreferences.value));
      
      if (result.company_logo) previewLogo.value = result.company_logo;
      if (result.payment_fqdn_status === "success") verifiedPaymentFqdn.value = result.payment_fqdn;
      
      return result;
    } catch (err) {
      // Ignore 404s (settings don't exist yet for new tenants)
      if (!HttpError.isNotFound(err)) {
        triggerNotif({
          type: "error",
          title: "Error fetching email settings",
          bodyText: "An error occurred while fetching your email settings. Please try again, or contact us for support."
        });
      }
      throw err;
    } finally {
      loading.value = false;
      loadingStates.value.fetchingSettings = false;
    }
  }

  function uploadImage(imageData: ImageData | null) {
    companyLogoImage.value = imageData;
    previewLogo.value = imageData ? imageData.dataUrl : "";
  }
  
  function addHttpsToUrl(value: string) {
    let finalUrl: string | null = null;

    if (isDns(value)) finalUrl = `https://${value}`;
    
    try {
      const url = new URL(value);
      // If the protocol is not http: or https:, force it to https:
      if (!/^https?:$/.test(url.protocol)) url.protocol = "https:";
      finalUrl = url.toString();
    } catch { finalUrl = null }
    
    if (finalUrl) emailPreferences.value.companyWebsite = finalUrl;
  }

  function setDefaultPaymentFQDN(companyWebsite: string) {
    if (isUrl(companyWebsite.trim())) {
      const url = new URL(companyWebsite);
      const domain = url.hostname;
      const arr = domain.split(".");
      if (arr[0] === "www") {
        arr.shift();
      }
      arr.splice(0, 0, "payments");
      const paymentFqdn = arr.join(".");
      emailPreferences.value.paymentFqdn = paymentFqdn;
      
      if (verifiedPaymentFqdn.value === paymentFqdn) {
        emailPreferences.value.paymentFqdnStatus = "success";
      } else {
        emailPreferences.value.paymentFqdnStatus = "";
      }
    }
  }

  function updateEmailPreferences() {
    loading.value = true;
    loadingStates.value.updatingSettings = true;

    const formData = new FormData();

    const formFields = {
      company_website: emailPreferences.value.companyWebsite,
      payment_fqdn: emailPreferences.value.paymentFqdn,
      payment_fqdn_status: emailPreferences.value.paymentFqdnStatus,
      mailing_address: emailPreferences.value.mailingAddress,
      mail_server: emailPreferences.value.mailServer,
      dkim_from_name: emailPreferences.value.DkimFromName || "",
      dkim_email_address: emailPreferences.value.DkimEmailAddress || "",
      company_name: currentTenant.value.name || "",
    };
    Object.entries(formFields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const rawSmtpSettings = mapToRawSmtpSettings(emailPreferences.value.smtpSettings);
    const rawDefaultSettings = {
      data: emailPreferences.value.defaultSettings.data,
      dkim_email_domain: emailPreferences.value.defaultSettings.dkimEmailDomain,
      dkim_email_prefix: emailPreferences.value.defaultSettings.dkimEmailPrefix,
      dkim_from_name: emailPreferences.value.defaultSettings.dkimFromName
    };
    formData.append("smtp_settings", JSON.stringify(rawSmtpSettings));
    formData.append("default_settings", JSON.stringify(rawDefaultSettings));

    if (companyLogoImage.value) {
      formData.append("company_logo", JSON.stringify(companyLogoImage.value));
      formData.append("company_logo_type",
        (companyLogoImage.value.dataUrl && companyLogoImage.value.dataUrl.startsWith("data:image/"))
          ? companyLogoImage.value.dataUrl.split(";")[0].replace("data:", "")
          : "image/png"
      );
    } else formData.append("company_logo", "");

    return saveEmailPreferences(currentTenant.value.id, formData)
      .then((result) => {
        triggerNotif({
          type: "success",
          title: "Email settings updated successfully"
        });

        initialEmailPreferences.value = mapToEmailSettings(result.data);
        emailPreferences.value = JSON.parse(JSON.stringify(initialEmailPreferences.value));

        if (result.data.company_logo) {
          previewLogo.value = result.data.company_logo;
        }

        getGatewaySettings(currentTenant.value.id)
          .then((gatewayResult) => {
            updateCwaccSettings(currentTenant.value.id, {
              is_configured_email_settings: result.data.is_configured,
              is_configured_payment_settings: gatewayResult?.is_configured ?? false
            }).catch((err) => {
              console.error("Failed to update Cwacc settings:", err);
            });
          })
          .catch((err) => {
            console.error("Failed to fetch gateway settings for Cwacc update:", err);
            updateCwaccSettings(currentTenant.value.id, {
              is_configured_email_settings: result.data.is_configured,
              is_configured_payment_settings: false
            }).catch((cwaccErr) => {
              console.error("Failed to update Cwacc settings:", cwaccErr);
            });
          });

        loading.value = false;
        loadingStates.value.updatingSettings = false;
        return result;
      })
      .catch((error) => {
        triggerNotif({
          type: "error",
          title: "Error updating email settings",
          bodyText: "An error occurred while updating your email settings. Please try again, or contact us for support."
        });
        loading.value = false;
        loadingStates.value.updatingSettings = false;
        throw error;
      });
  }

  function testEmailPreferences() {
    loading.value = true;
    loadingStates.value.testingEmail = true;
    
    return sendTestEmail(currentTenant.value.id)
      .then((result) => {
        triggerNotif({
          type: "success",
          title: "Test email sent",
          bodyText: result.msg
        });
        loading.value = false;
        loadingStates.value.testingEmail = false;
        return result;
      })
      .catch((error) => {
        triggerNotif({
          type: "error",
          title: "Error testing email settings",
          bodyText: "An error occurred while testing your email settings. Please try again, or contact us for support."
        });
        loading.value = false;
        loadingStates.value.testingEmail = false;
        throw error;
      });
  }

  function verifyPaymentFQDN() {
    loading.value = true;
    loadingStates.value.verifyingPaymentFqdn = true;
    
    return verifyEmailDomain(currentTenant.value.id, emailPreferences.value.paymentFqdn)
      .then((result) => {
        if (result.verified_status) {
          emailPreferences.value.paymentFqdnStatus = result.verified_status;
          verifiedPaymentFqdn.value = emailPreferences.value.paymentFqdn;
          
          if (result.verified_status === "success") {
            triggerNotif({
              type: "success",
              title: "Payment FQDN verified successfully"
            });
          } else if (result.verified_status === "failure") {
            triggerNotif({
              type: "error",
              title: "Payment FQDN verification failed",
              bodyText: "Please check your DNS settings."
            });
          }
        }
        return result;
      })
      .catch((error) => {
        // Check if this is the specific "Payment Domain has been used" error
        if (error.message && error.message.includes("Payment Domain has been used")) {
          triggerNotif({
            type: "error",
            title: "Payments domain already in use",
            bodyText: "The Payments domain you entered is already in use. Please choose a different domain."
          });
        } else {
          triggerNotif({
            type: "error",
            title: "Error verifying Payments FQDN",
            bodyText: "An error occurred while verifying your Payments FQDN. Please try again, or contact us for support."
          });
        }
        throw error;
      })
      .finally(() => {
        loading.value = false;
        loadingStates.value.verifyingPaymentFqdn = false;
      });
  }

  function checkPaymentFQDNVerification() {
    if (!verifiedPaymentFqdn.value) return;

    if (emailPreferences.value.paymentFqdn !== verifiedPaymentFqdn.value) {
      emailPreferences.value.paymentFqdnStatus = "";
    } else emailPreferences.value.paymentFqdnStatus = "success";
  }

  function createEmailPreferenceDKIM() {
    if (!dnsDomain.value) {
      triggerNotif({
        type: "error",
        title: "Domain is required",
        bodyText: "Please enter a domain name."
      });
      return Promise.reject(new Error("Domain is required"));
    }
    
    loadingStates.value.addingDomain = true;
    
    return addDkimDomain(currentTenant.value.id, dnsDomain.value)
      .then((result) => {
        if (!emailPreferences.value.defaultSettings.data) {
          emailPreferences.value.defaultSettings.data = [result.dkim];
        } else {
          emailPreferences.value.defaultSettings.data.push(result.dkim);
        }
        
        dnsDomain.value = "";
        loadingStates.value.addingDomain = false;
        
        triggerNotif({
          type: "success",
          title: "Domain added successfully"
        });
        
        return result;
      })
      .catch((error) => {
        triggerNotif({
          type: "error",
          title: "Error adding domain",
          bodyText: "An error occurred while adding the domain. Please try again, or contact us for support."
        });
        loadingStates.value.addingDomain = false;
        throw error;
      });
  }

  function deleteEmailPreferenceDKIM(id: string) {
    loadingStates.value.deletingDomains[id] = true;
    
    return removeDkimDomain(currentTenant.value.id, id)
      .then((result) => {
        const index = emailPreferences.value.defaultSettings.data.findIndex(item => item.id === id);
        if (index !== -1) {
          emailPreferences.value.defaultSettings.data.splice(index, 1);
        }
        
        loadingStates.value.deletingDomains[id] = false;
        
        triggerNotif({
          type: "success",
          title: "Domain removed successfully"
        });
        
        return result;
      })
      .catch((error) => {
        triggerNotif({
          type: "error",
          title: "Error removing domain",
          bodyText: "An error occurred while removing the domain. Please try again, or contact us for support."
        });
        loadingStates.value.deletingDomains[id] = false;
        throw error;
      });
  }

  function verifyEmailPreferenceDKIM(id: string) {
    loadingStates.value.verifyingDomains[id] = true;
    
    return verifyDkimDomain(currentTenant.value.id, id)
      .then((result) => {
        const index = emailPreferences.value.defaultSettings.data.findIndex(item => item.id === id);
        if (index !== -1) {
          emailPreferences.value.defaultSettings.data[index].data = result.dkim.data;
          emailPreferences.value.defaultSettings.data[index].verified = result.dkim.verified;
          emailPreferences.value.defaultSettings.data[index].sendgrid_verified = result.dkim.sendgrid_verified;
          emailPreferences.value.defaultSettings.data[index].last_verified = result.dkim.last_verified;
        }
        
        loadingStates.value.verifyingDomains[id] = false;
        
        triggerNotif({
          type: result.dkim.verified ? "success" : "error",
          title: result.dkim.verified ? "Domain verification completed" : "Domain verification unsuccessful",
          bodyText: result.dkim.verified ? "Domain verified successfully." : "Please check your DNS settings."
        });
        
        return result;
      })
      .catch((error) => {
        triggerNotif({
          type: "error",
          title: "Error verifying domain",
          bodyText: "An error occurred while verifying the domain. Please try again, or contact us for support."
        });
        loadingStates.value.verifyingDomains[id] = false;
        throw error;
      });
  }

  return {
    companyLogoImage,
    previewLogo,
    dnsDomain,
    loading,
    loadingStates,
    verifiedPaymentFqdn,
    initialEmailPreferences,
    emailPreferences,
    getEmailSettings,
    uploadImage,
    addHttpsToUrl,
    setDefaultPaymentFQDN,
    updateEmailPreferences,
    testEmailPreferences,
    verifyPaymentFQDN,
    checkPaymentFQDNVerification,
    createEmailPreferenceDKIM,
    deleteEmailPreferenceDKIM,
    verifyEmailPreferenceDKIM
  };
});
